/**
 * useTraderProfile Hook
 *
 * Determines the trader's "personality" profile based on trading patterns.
 * Confidence thresholds are intentionally difficult to achieve 100%.
 */

import { useMemo } from "react";
import type {
  CompletedTrade,
  TraderProfile,
  AdvancedStats,
  TimeAnalysis,
  AssetAnalysis,
  BehaviorAnalysis,
} from "@nordex/core";

/**
 * Profile definitions with criteria
 * Note: Confidence calculations are designed to make 100% very difficult to achieve
 */
const PROFILE_DEFS: Array<{
  id: string;
  label: string;
  description: string;
  icon: string;
  criteria: string;
  check: (data: ProfileCheckData) => number; // Returns confidence 0-100
}> = [
  {
    id: "night_owl",
    label: "Night Owl",
    description: "You dominate the late-night markets when others are asleep.",
    icon: "🦉",
    criteria: ">70% of trades between 10PM-6AM UTC",
    check: ({ timeAnalysis }) => {
      const nightHours = [22, 23, 0, 1, 2, 3, 4, 5];
      const totalTrades = timeAnalysis.hourly.reduce((s, h) => s + h.trades, 0);
      if (totalTrades < 50) return 0; // Need more trades to establish pattern

      const nightTrades = nightHours.reduce(
        (sum, h) => sum + (timeAnalysis.hourly[h]?.trades || 0),
        0
      );
      const nightPercent = (nightTrades / totalTrades) * 100;
      // Need 70%+ for any match, scales to 100% at 95%
      if (nightPercent < 70) return 0;
      return Math.min(((nightPercent - 70) / 25) * 100, 100);
    },
  },
  {
    id: "early_bird",
    label: "Early Bird",
    description: "You catch the worm with morning trades before the crowd.",
    icon: "🌅",
    criteria: ">70% of trades between 5AM-11AM UTC",
    check: ({ timeAnalysis }) => {
      const morningHours = [5, 6, 7, 8, 9, 10];
      const totalTrades = timeAnalysis.hourly.reduce((s, h) => s + h.trades, 0);
      if (totalTrades < 50) return 0;

      const morningTrades = morningHours.reduce(
        (sum, h) => sum + (timeAnalysis.hourly[h]?.trades || 0),
        0
      );
      const morningPercent = (morningTrades / totalTrades) * 100;
      // Need 70%+ for any match, scales to 100% at 95%
      if (morningPercent < 70) return 0;
      return Math.min(((morningPercent - 70) / 25) * 100, 100);
    },
  },
  {
    id: "scalper",
    label: "Scalper Supreme",
    description: "Lightning-fast trades are your specialty. In and out.",
    icon: "⚡",
    criteria: "Avg trade duration <2 min with >100 trades",
    check: ({ stats }) => {
      if (stats.totalTrades < 100) return 0; // Need substantial volume
      const avgMinutes = stats.avgTradeDurationMs / (60 * 1000);
      if (avgMinutes >= 2) return 0;
      // Sub-30-second avg = 100%, 2 min = 0%
      return Math.min(((2 - avgMinutes) / 1.5) * 100, 100);
    },
  },
  {
    id: "patient_trader",
    label: "Patient Trader",
    description: "You let your trades breathe. Time is on your side.",
    icon: "🧘",
    criteria: "Avg hold time >24 hours",
    check: ({ stats }) => {
      if (stats.totalTrades < 30) return 0;
      const avgHours = stats.avgTradeDurationMs / (60 * 60 * 1000);
      // Minimum 24 hours for any confidence
      if (avgHours < 24) return 0;
      // 24 hours = ~30%, 48 hours = ~60%, 72+ hours = ~90-100%
      return Math.min(((avgHours - 24) / 48) * 70 + 30, 100);
    },
  },
  {
    id: "meme_degen",
    label: "Meme Coin Degen",
    description: "You ride the meme waves. High risk, high reward.",
    icon: "🎲",
    criteria: ">70% volume in meme coins with profit",
    check: ({ assetAnalysis }) => {
      const totalVolume = assetAnalysis.byCategory.reduce(
        (s, c) => s + c.volume,
        0
      );
      if (totalVolume < 10000) return 0; // Need meaningful volume

      const memeCategory = assetAnalysis.byCategory.find(
        (c) => c.id === "meme"
      );
      const memeVolume = memeCategory?.volume || 0;
      const memePnl = memeCategory?.pnl || 0;
      const memePercent = (memeVolume / totalVolume) * 100;
      
      // Must be profitable in memes and have >70% volume
      if (memePercent < 70 || memePnl <= 0) return 0;
      return Math.min(((memePercent - 70) / 25) * 70 + 30, 100);
    },
  },
  {
    id: "major_only",
    label: "Blue Chip Trader",
    description: "You stick to the majors. BTC and ETH are your playground.",
    icon: "🏛️",
    criteria: ">85% volume in major tokens with profit",
    check: ({ assetAnalysis }) => {
      const totalVolume = assetAnalysis.byCategory.reduce(
        (s, c) => s + c.volume,
        0
      );
      if (totalVolume < 10000) return 0;

      const majorCategory = assetAnalysis.byCategory.find(
        (c) => c.id === "major"
      );
      const majorVolume = majorCategory?.volume || 0;
      const majorPnl = majorCategory?.pnl || 0;
      const majorPercent = (majorVolume / totalVolume) * 100;
      
      // Must be profitable and have >85% volume
      if (majorPercent < 85 || majorPnl <= 0) return 0;
      return Math.min(((majorPercent - 85) / 12) * 70 + 30, 100);
    },
  },
  {
    id: "short_specialist",
    label: "Bear Hunter",
    description: "You profit when others panic. Shorts are your weapon.",
    icon: "🐻",
    criteria: ">75% short trades with >60% win rate",
    check: ({ behavior, stats }) => {
      if (stats.totalTrades < 50) return 0;
      const shortPercent =
        (behavior.shortStats.trades / stats.totalTrades) * 100;
      
      // Need 75%+ shorts with 60%+ win rate
      if (shortPercent < 75 || behavior.shortStats.winRate < 60) return 0;
      
      // Confidence based on both metrics
      const shortScore = ((shortPercent - 75) / 20) * 50; // 0-50 based on short %
      const winScore = ((behavior.shortStats.winRate - 60) / 30) * 50; // 0-50 based on win rate
      return Math.min(shortScore + winScore + 20, 100);
    },
  },
  {
    id: "long_specialist",
    label: "Bull Rider",
    description: "You ride the bulls to the moon. Longs are your game.",
    icon: "🐂",
    criteria: ">80% long trades with >60% win rate",
    check: ({ behavior, stats }) => {
      if (stats.totalTrades < 50) return 0;
      const longPercent = (behavior.longStats.trades / stats.totalTrades) * 100;
      
      // Need 80%+ longs with 60%+ win rate
      if (longPercent < 80 || behavior.longStats.winRate < 60) return 0;
      
      const longScore = ((longPercent - 80) / 18) * 50;
      const winScore = ((behavior.longStats.winRate - 60) / 30) * 50;
      return Math.min(longScore + winScore + 20, 100);
    },
  },
  {
    id: "sniper",
    label: "Low-Risk Sniper",
    description: "Precision trading with excellent risk management.",
    icon: "🎯",
    criteria: "Sharpe ratio >2.5 with >50 trades",
    check: ({ stats }) => {
      if (stats.totalTrades < 50 || stats.sharpeRatio === null) return 0;
      if (stats.sharpeRatio < 2.5) return 0;
      // Sharpe of 2.5 = 30%, 4.0 = ~70%, 5.0+ = ~100%
      return Math.min(((stats.sharpeRatio - 2.5) / 2.5) * 70 + 30, 100);
    },
  },
  {
    id: "whale",
    label: "Whale Trader",
    description: "You move markets with your size. Big trades, big moves.",
    icon: "🐋",
    criteria: "Avg trade size >$25,000",
    check: ({ stats }) => {
      if (stats.totalTrades < 20) return 0;
      const avgSize = stats.totalVolume / stats.totalTrades;
      // Minimum $25k for any confidence
      if (avgSize < 25000) return 0;
      // $25k = 30%, $50k = 65%, $100k+ = ~100%
      return Math.min(((avgSize - 25000) / 75000) * 70 + 30, 100);
    },
  },
  {
    id: "consistent",
    label: "Steady Eddie",
    description: "Consistency is your edge. Reliable profits, minimal variance.",
    icon: "📊",
    criteria: "Profit factor >2.0 for 60+ days with <10% drawdown",
    check: ({ stats }) => {
      if (stats.tradingDays < 60) return 0;
      if (stats.profitFactor < 2.0 || stats.maxDrawdownPercent > 10) return 0;
      
      // Factor in both profit factor and low drawdown
      const pfScore = Math.min(((stats.profitFactor - 2) / 2) * 50, 50);
      const ddScore = Math.min(((10 - stats.maxDrawdownPercent) / 8) * 50, 50);
      return Math.min(pfScore + ddScore + 20, 100);
    },
  },
  {
    id: "diversified",
    label: "Portfolio Master",
    description: "You spread your bets across the market. Diversification is key.",
    icon: "🌐",
    criteria: "Profitable in 8+ markets across 4+ categories",
    check: ({ assetAnalysis }) => {
      const profitableMarkets = assetAnalysis.byMarket.filter(
        (m) => m.trades >= 5 && m.pnl > 0
      ).length;
      const profitableCategories = assetAnalysis.byCategory.filter(
        (c) => c.trades >= 5 && c.pnl > 0
      ).length;
      
      // Need 8+ profitable markets and 4+ categories
      if (profitableMarkets < 8 || profitableCategories < 4) return 0;
      
      const marketScore = Math.min((profitableMarkets - 8) * 5, 50);
      const catScore = Math.min((profitableCategories - 4) * 12, 50);
      return Math.min(marketScore + catScore + 30, 100);
    },
  },
];

interface ProfileCheckData {
  trades: CompletedTrade[];
  stats: AdvancedStats;
  timeAnalysis: TimeAnalysis;
  assetAnalysis: AssetAnalysis;
  behavior: BehaviorAnalysis;
}

/**
 * Determine trader profiles from trading data
 */
function determineProfiles(data: ProfileCheckData): TraderProfile[] {
  const profiles: TraderProfile[] = [];

  for (const def of PROFILE_DEFS) {
    const confidence = def.check(data);
    if (confidence > 0) {
      profiles.push({
        id: def.id,
        label: def.label,
        description: def.description,
        icon: def.icon,
        criteria: def.criteria,
        confidence: Math.round(confidence),
      });
    }
  }

  // Sort by confidence descending
  profiles.sort((a, b) => b.confidence - a.confidence);

  return profiles;
}

/**
 * Hook for trader profile determination
 */
export function useTraderProfile(
  trades: CompletedTrade[],
  stats: AdvancedStats,
  timeAnalysis: TimeAnalysis,
  assetAnalysis: AssetAnalysis,
  behavior: BehaviorAnalysis
): {
  profiles: TraderProfile[];
  primaryProfile: TraderProfile | null;
} {
  const profiles = useMemo(
    () =>
      determineProfiles({
        trades,
        stats,
        timeAnalysis,
        assetAnalysis,
        behavior,
      }),
    [trades, stats, timeAnalysis, assetAnalysis, behavior]
  );

  const primaryProfile = profiles.length > 0 ? profiles[0] : null;

  return {
    profiles,
    primaryProfile,
  };
}
